import type { Vignette, Medium } from '../../domain/types';
import { useT } from '../../i18n';
import { HtmlText } from '../../ui/HtmlText';
import { VignetteMaterial } from './VignetteMaterial';
import styles from './Sheets.module.css';

/**
 * Der Inhalt der Fallvignette — an **zwei** Stellen gezeigt: im Dialog über dem Bogen
 * (`VignetteDialog`) und im angehefteten Seitenfeld daneben (`bogen/VignettePanel`).
 *
 * ⚠️ **Eine Wahrheit, zwei Rahmen.** Stünden die Blöcke zweimal da, liefen sie auseinander,
 * sobald jemand einen dritten dazunimmt — und der Prüfer sähe je nach Weg etwas anderes vom
 * selben Fall. Dieselbe Begründung wie bei `domain/abgeleitet.ts`.
 */
export function VignetteInhalt({
  vignette,
  medien,
  examId,
}: {
  vignette: Vignette | undefined;
  medien: Medium[];
  examId: string;
}) {
  const t = useT();
  return (
    <>
      {vignette ? (
        <Block label={t.vignette.situation} text={vignette.lead} html={vignette.html} />
      ) : null}
      {vignette && vignette.task !== '' ? (
        <Block label={t.vignette.task} text={vignette.task} accent />
      ) : null}
      {/* Die Anleitung für die Prüferin steht zuletzt und abgesetzt: sie ist nicht Teil des
          Falls, sondern sagt, was *sie* tun soll. Fehlt sie, fehlt auch der Block. */}
      {vignette?.instructions ? (
        <Block label={t.vignette.instructions} text={vignette.instructions} accent />
      ) : null}
      <VignetteMaterial examId={examId} medien={medien} />
    </>
  );
}

function Block({
  label,
  text,
  html,
  accent,
}: {
  label: string;
  text: string;
  /** Liegt das HTML vor, wird es gezeigt — mit Absätzen, nicht in einer Zeile. */
  html?: string;
  accent?: boolean;
}) {
  return (
    <div className={styles.block} data-accent={!!accent}>
      <div className={styles.blockLabel}>{label}</div>
      {html ? (
        <HtmlText className={styles.blockHtml} text={html} absaetze />
      ) : (
        <div className={styles.blockText}>{text}</div>
      )}
    </div>
  );
}
